import {
    ApartmentAtAddress,
    MunicipalitySectionInUseError,
    OwnerGroupInUseError,
    TagInUseError,
} from "@/models/responses";

/**
 * The 409 body `DELETE /api/admin/addresses/:id` answers with when the building
 * still has in-service apartments (address-normalization/AS-006). Declared here
 * rather than generated from a JSON schema because the address dimension has no
 * response schema of its own — the shape is the same `errors[].blockingApartments`
 * envelope the tag / municipality-section / owner-group refusals use.
 */
export interface AddressInUseError {
    errors: Array<{ message: string; blockingApartments: ApartmentAtAddress[] }>;
}

/**
 * Shape shared by the three "option in use" 409 payloads (`TagInUseError`,
 * `MunicipalitySectionInUseError`, `OwnerGroupInUseError`). Each payload
 * surfaces a top-level `errors` array whose first entry carries a
 * `blockingApartments` list. Centralised here so the three management pages
 * (and any future option-deletion page) detect the structure uniformly
 * instead of redefining a per-dimension predicate.
 */
type GenericInUseError = {
    errors: Array<{ blockingApartments: unknown[] }>;
};

/**
 * Read the message from a typed in-use error body or a generic RTK Query
 * error envelope (`{ data: { errors: [...] } }` or `{ errors: [...] }`).
 * Returns `undefined` when neither shape matches — caller falls back to a
 * generic copy.
 */
export function extractErrorMessage(error: unknown): string | undefined {
    if (error && typeof error === "object" && "errors" in error) {
        const errs = (error as { errors?: { message?: string }[] }).errors;
        if (Array.isArray(errs) && errs.length > 0 && typeof errs[0]?.message === "string") {
            return errs[0].message;
        }
    }
    if (
        error &&
        typeof error === "object" &&
        "data" in error &&
        (error as { data: unknown }).data &&
        typeof (error as { data: unknown }).data === "object"
    ) {
        const data = (error as { data: { errors?: { message?: string }[] } }).data;
        const errs = data.errors;
        if (Array.isArray(errs) && errs.length > 0 && typeof errs[0]?.message === "string") {
            return errs[0].message;
        }
    }
    return undefined;
}

/**
 * Generic structural check: an RTK Query error is an "option in use" 409
 * when its body is `{ errors: [{ blockingApartments: [...] }, ...] }`.
 * RTK Query's `transformErrorResponse` for the delete mutations returns the
 * typed body directly, so discrimination is purely structural.
 *
 * Per-dimension type narrowing is provided by the thin wrappers below
 * (`isTagInUseError`, `isMunicipalitySectionInUseError`,
 * `isOwnerGroupInUseError`), which cast through `unknown` to the precise
 * domain type once the structure has been validated.
 */
export function isOptionInUseError(err: unknown): err is GenericInUseError {
    if (!err || typeof err !== "object") return false;
    const candidate = err as { errors?: unknown };
    if (!Array.isArray(candidate.errors) || candidate.errors.length === 0) return false;
    const first = candidate.errors[0] as { blockingApartments?: unknown };
    return (
        typeof first === "object" &&
        first !== null &&
        Array.isArray(first.blockingApartments)
    );
}

export function isTagInUseError(err: unknown): err is TagInUseError {
    return isOptionInUseError(err);
}

export function isMunicipalitySectionInUseError(
    err: unknown,
): err is MunicipalitySectionInUseError {
    return isOptionInUseError(err);
}

export function isOwnerGroupInUseError(err: unknown): err is OwnerGroupInUseError {
    return isOptionInUseError(err);
}

export function isAddressInUseError(err: unknown): err is AddressInUseError {
    return isOptionInUseError(err);
}
